/**
 * Number formatting utilities for US format (, as thousand separator, . as decimal)
 * Example: 1,234,567.89
 */

/**
 * Format a number for display with US formatting
 * @param {number|string} value - The number to format
 * @param {boolean} isInteger - Whether this is an integer (no decimals)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, isInteger = false) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const str = value.toString()

  if (isInteger) {
    // For integers, just add thousand separators, no decimals
    const cleanValue = str.replace(/[^\d-]/g, '')
    if (!cleanValue) return ''

    const isNegative = cleanValue.startsWith('-')
    const positiveValue = isNegative ? cleanValue.substring(1) : cleanValue

    // Add thousand separators (US format: comma)
    const formatted = positiveValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return isNegative ? '-' + formatted : formatted
  } else {
    // For decimals, preserve user input format
    const num = parseFloat(str.replace(/,/g, ''))
    if (isNaN(num)) {
      return str // Return original if can't parse
    }

    // Split by period (decimal separator)
    const parts = str.split('.')
    const integerPart = parts[0] || ''
    const decimalPart = parts[1] || ''

    // Clean and format integer part
    const cleanInteger = integerPart.replace(/[^\d-]/g, '')
    if (!cleanInteger) return ''

    const isNegative = cleanInteger.startsWith('-')
    const positiveInteger = isNegative ? cleanInteger.substring(1) : cleanInteger
    const formattedInteger = positiveInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    let result = isNegative ? '-' + formattedInteger : formattedInteger

    // Add decimal part if it exists
    if (parts.length > 1) {
      // Limit to 2 decimal places
      const limitedDecimal = decimalPart.substring(0, 2)
      result += '.' + limitedDecimal
    }

    return result
  }
}

/**
 * Parse a US formatted number string back to a number
 * @param {string} formattedValue - The formatted number string
 * @returns {number|null} Parsed number or null if invalid
 */
export const parseFormattedNumber = (formattedValue) => {
  if (!formattedValue || typeof formattedValue !== 'string') {
    return null
  }

  // Remove thousand separators (,) - decimal separator (.) stays
  const cleanValue = formattedValue.replace(/,/g, '') // Remove thousand separators

  const parsed = parseFloat(cleanValue)
  return isNaN(parsed) ? null : parsed
}

/**
 * Validate if a string is a valid US formatted number
 * @param {string} value - The value to validate
 * @returns {boolean} True if valid format
 */
export const isValidFormattedNumber = (value) => {
  if (!value || typeof value !== 'string') {
    return false
  }

  // US number format regex
  // Allows: 1,234,567.89 or 1234.89 or 1234 or 1,234
  const usNumberRegex = /^-?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/

  return usNumberRegex.test(value)
}

/**
 * Format number input as user types (for controlled inputs)
 * @param {string} value - Current input value
 * @param {string} previousValue - Previous input value
 * @param {boolean} allowDecimals - Whether to allow decimal places
 * @returns {string} Formatted value
 */
export const formatNumberInput = (value, previousValue = '', allowDecimals = true) => {
  if (!value) return ''

  if (!allowDecimals) {
    // For integers: preserve user input but add thousand separators
    let cleanValue = value.replace(/[^\d-]/g, '')

    // Handle negative sign
    const isNegative = cleanValue.startsWith('-')
    if (isNegative) {
      cleanValue = cleanValue.substring(1)
    }

    // Only add thousand separators if there are more than 3 digits
    let formattedValue = cleanValue
    if (cleanValue.length > 3) {
      formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    // Add negative sign back
    if (isNegative) {
      formattedValue = '-' + formattedValue
    }

    return formattedValue
  } else {
    // For decimals: preserve user input with formatting
    let cleanValue = value.replace(/[^\d.-]/g, '')

    // Handle negative sign
    const isNegative = cleanValue.startsWith('-')
    if (isNegative) {
      cleanValue = cleanValue.substring(1)
    }

    // Only allow one decimal point
    const decimalCount = (cleanValue.match(/\./g) || []).length
    if (decimalCount > 1) {
      // Keep only the first decimal point
      const firstDecimalIndex = cleanValue.indexOf('.')
      cleanValue = cleanValue.substring(0, firstDecimalIndex + 1) + cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '')
    }

    // Split by period (decimal separator)
    const parts = cleanValue.split('.')
    let integerPart = parts[0] || ''
    let decimalPart = parts[1] || ''

    // Limit decimal part to 2 digits but preserve what user typed
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2)
    }

    // Add thousand separators to integer part only if there are more than 3 digits
    let formattedInteger = integerPart
    if (integerPart.length > 3) {
      formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    // Combine parts - preserve decimal point if user typed it
    let result = formattedInteger
    if (parts.length > 1) {
      result += '.' + decimalPart
    }

    // Add negative sign back
    if (isNegative) {
      result = '-' + result
    }

    return result
  }
}

/**
 * Get the raw numeric value from a formatted input
 * @param {string} formattedValue - The formatted input value
 * @returns {string} Raw numeric value suitable for form submission
 */
export const getRawNumericValue = (formattedValue) => {
  if (!formattedValue) return ''

  // Convert US format to standard format
  return formattedValue.replace(/,/g, '') // Remove thousand separators, keep decimal separator
}
